export class TDInputOTP extends HTMLElement {
  /**
   * Specifies the attributes to observe for changes.
   * @returns {string[]} The list of attribute names to observe.
   */
  static get observedAttributes() {
    return ['value']
  }

  /** @type {HTMLInputElement[]} */
  _inputRefs = []

  /** @type {string[]} */
  _internalCode = new Array(6).fill('')

  /** @type {boolean} */
  _isControlled = false

  constructor() {
    super()

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.innerHTML = `
      <style>
        :host {
          position: relative;
        }
  
        form {
          -webkit-font-smoothing: antialiased;
          position: relative;
          display: flex;
          gap: var(--otp-form-gap, 0.5rem);
          justify-content: center;
          align-items: center;
          max-width: var(--otp-form-width, 335px);
          margin: var(--otp-form-margin, 0 auto 30px);
          padding: 6px 0 0;
          transition: all .3s ease-in-out;
        }

        input {
          border-width: var(--input-otp-border-width, 0.5px);
          border-style: var(--input-otp-border-style, solid);
          border-color: var(--input-otp-border-color, #024);
          border-radius: var(--input-otp-radius, 8px);
          box-shadow: var(--input-otp-shadow, none);
          background-color: var(--input-otp-background, #fff);
          color: var(--input-otp-color, #024);
          flex: 1;
          font-family: var(--input-otp-font, "DM Sans", sans-serif);
          font-size: var(--input-otp-font-size, 16px);
          font-style: normal;
          font-weight: var(--input-otp-font-weight, 400);
          line-height: 110%;
          margin: 0;
          min-width: var(--input-otp-min-width, 0);
          padding: var(--input-otp-padding, 14.5px 16px);
          outline: none;
          text-align: center;
        }

        input:focus {
          border-width: var(--input-otp-border-width-focus, 2px);
          border-style: var(--input-otp-border-style-focus, solid);
          border-color: var(--input-otp-border-color-focus, #468f35);
          padding: var(--input-otp-padding-focus, 13px 14.5px);
        }
        
        input.error {
          border-color: var(--input-otp-border-error, #f00f00);
        }

        .small-loader {
          position: absolute;
          bottom: -10px;
          width: calc(100% + 10px);
          height: calc(100% + 20px);
          left: -10px;
          background: #fff;
          padding: 5px;
          opacity: 0;
          pointer-events: none;
          transition: opacity .15s ease-in-out;
        }

        .small-loader svg {
          height: 60px;
          width: 100%;
        }

        div.error {
          color: var(--input-otp-error-color, #f00f00);
          font-family: var(--input-otp-error-font, "DM Sans", sans-serif);
          font-size: var(--input-otp-error-font-size, 14px);
          text-align: center;
          position: absolute;
          left: 0;
          top: 100%;
          width: 100%;
          padding: 5px;
        }
    
        .reset {
          display: flex;
          justify-content: center;
          flex-direction: column;
          padding: 2px 15px 0;
          gap: .5rem;
          max-width: 335px;
          color: #4d4f53;
          font-family: "DM Sans", sans-serif;
          font-size: var(--otp-reset-font-size, 14px);
          margin: 0 auto 5px;
          text-align: center;
        }

        .reset button:disabled {
          opacity: .65;
          cursor: default;
        }

        .reset button {
          cursor: pointer;
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          color: #abacae;
          border: none;
          font-family: var(--input-otp-reset-font, "DM Sans", sans-serif);
          font-size: var(--input-otp-reset-button-font-size, 14px);
          font-weight: var(--input-otp-reset-button-font-weight, 700);
          text-transform: uppercase;
          padding: 5px 0;
          margin-bottom: -12px;
          margin-top: -14px;
          display: flex;
          gap: 5px;
          align-items: center;
        }

        .reset button svg {
          width: 12px;
          height: 12px;
        }

        .reset .instructions {
          margin-bottom: 15px; 
        }

        .reset .instructions span {
          white-space: nowrap;
          }

        .reset-buttons {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          position: relative;
          margin: 26px 0 22px;
        }

        .reset-buttons span {
          position: absolute;
          right: 0;
          top: 10px;
          font-size: 14px;
          width: 113px;
        }
        .spacer {
          font-size: var(--otp-spacer-font-size, 16px);
          padding: var(--otp-spacer-padding, 0 10px);
        }

        form.onboarding .spacer {
          color: #828282;
        }

        form.onboarding input {
          background-color: #f2f2f2;
          border: 0.5px solid #5F6164;
          height: 9px;
          min-width: 38px;
          border-radius: 4px;
          padding: 14.5px 0;
        }

        form.onboarding {
          gap: 12px;
          margin-bottom: 20px;
        }

        :host(.onboarding) .reset {
          max-width: none;
          margin: 0;
          padding: 0;
        }

        :host(.onboarding) .reset button {
          color: #828282;
          font-weight: 400;
          font-size: 10px;
        }

        :host(.onboarding) .reset button:nth-child(2) {
          flex-direction: row-reverse;
        }

        :host(.onboarding) .reset .instructions {
          color: #5F6164;
          font-weight: 500;
          font-size: 12px;

        }
        
        @media (min-width: 766px) {
          form {
            margin: var(--otp-form-margin-lg, 0 auto 30px);
          }

          form,
          .reset {
            max-width: var(--otp-form-width-lg, 460px);
            gap: var(--otp-form-gap-lg, .5rem);
          }

          .reset {
            font-size: var(--otp-reset-font-size-lg, 14px);
          }
          .spacer {
            font-size: var(--otp-spacer-font-size-lg, 30px);
            padding: var(--otp-spacer-padding-lg, 0 10px);
          }
          input {
            border-radius: var(--input-otp-radius-lg, 10px);
            font-size: var(--input-otp-font-size-lg, 20px);
            padding: var(--input-otp-padding-lg, 19px 15px);
            border-width: var(--input-otp-border-width-lg, 1px);
            border-style: var(--input-otp-border-style-lg, solid);
            border-color: var(--input-otp-border-color-lg, #024);
            min-width: var(--input-otp-min-width-lg, 0);
          }

          input:focus {
            border-width: var(--input-otp-border-width-focus-lg, 2px);
            border-style: var(--input-otp-border-style-focus-lg, solid);
            border-color: var(--input-otp-border-color-focus-lg, #468f35);
            padding: var(--input-otp-padding-focus-lg, 17.5px 13.5px);
          }

          div.error {
            font-size: var(--input-otp-error-font-size-lg, 14px);
          }

          .reset {
            gap: 1rem;
            margin-bottom: 3px;
            padding-top: 8px;
          }
          
          .reset .instructions {
            margin-bottom: 0; 
          }

          form.onboarding input {
            height: 39px;
            min-width: 68px;
          }

          form.onboarding {
            gap: 18px;
            padding-top: 2px;
          }

          :host(.onboarding) .reset .instructions {
            font-size: 14px;
            font-weight: 500;
          }

          :host(.onboarding) .reset-buttons {
            margin-top: 38px;
          }

          :host(.onboarding) .reset button {
            font-size: 14px;
          }

          
        }
      </style>
      <form autocomplete="one-time-code" class=${this.className}>
        <div class="error"></div>
        <div class="loader small-loader">
          <svg id="loaderSvg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65 50">
            <rect fill="#468F35" stroke="#468F35" stroke-width="5" width="15" height="15" x="0" y="0">
              <animate attributeName="opacity" calcMode="spline" dur="1.8" values="1;0;1;" keySplines=".5 0 .5 1;.5 0 .5 1"
                repeatCount="indefinite" begin="-.4"></animate>
            </rect>
            <rect fill="#468F35" stroke="#468F35" stroke-width="5" width="15" height="15" x="25" y="0">
              <animate attributeName="opacity" calcMode="spline" dur="1.8" values="1;0;1;" keySplines=".5 0 .5 1;.5 0 .5 1"
                repeatCount="indefinite" begin="-.2"></animate>
            </rect>
            <rect fill="#468F35" stroke="#468F35" stroke-width="5" width="15" height="15" x="50" y="0">
              <animate attributeName="opacity" calcMode="spline" dur="1.8" values="1;0;1;" keySplines=".5 0 .5 1;.5 0 .5 1"
                repeatCount="indefinite" begin="0"></animate>
            </rect>
          </svg>
        </div>
      </form>
      <div class="reset">
        <div class="reset-container">
          <div class="instructions"></div>
          <div class="reset-buttons"></div>
        </div>
      </div>
    `

    let form = shadow.querySelector('form')
    if (!form) {
      // TODO: Investigate, this is a weird issue.
      const stubform = document.createElement('form')
      stubform.classList.add(this.className)
      //@ts-ignore
      stubform.autocomplete = 'one-time-code'
      shadow.appendChild(stubform)
      form = shadow.querySelector('form')
    }

    for (let i = 0; i < 6; i++) {
      const input = document.createElement('input')
      input.type = 'text'
      input.pattern = '[0-9]*'
      input.inputMode = 'numeric'
      input.setAttribute('autocorrect', 'off')
      input.autocapitalize = 'off'
      input.autocomplete = i === 0 ? 'one-time-code' : 'off'
      input.value = this._internalCode[i] || ''

      this._inputRefs[i] = input

      input.addEventListener('keydown', (e) => this._handleKeyDown(e, i))
      input.addEventListener('input', (e) => this._handleChange(e, i))
      input.addEventListener('paste', (e) => this._handlePaste(e))

      if (form) {
        form.appendChild(input)

        const spacer = document.createElement('span')
        spacer.classList.add('spacer')
        spacer.textContent = ` ${form.classList.contains('onboarding') ? '●' : '-'} `

        if (i === 2) {
          form.appendChild(spacer)
        }
      }
    }
  }

  /**
   * Gets the name of the input.
   * @returns {string} The value.
   */
  get name() {
    return this.getAttribute('name') || ''
  }

  /**
   * Gets the current value of the input.
   * @returns {string} The value.
   */
  get value() {
    return this._internalCode.join('')
  }

  /**
   * Gets the current class name of the input.
   * @returns {string} The class name.
   */
  get className() {
    return this.getAttribute('class') || ''
  }

  /**
   * Sets the value of the input.
   * @param {string} newVal - The value to set.
   */
  set value(newVal) {
    this.setAttribute('value', newVal)
  }

  /**
   * Callback invoked when an observed attribute changes.
   * Updates the internal value if the 'value' attribute has changed.
   * @param {string} name - The name of the attribute that changed.
   * @param {string} oldValue - The old value of the attribute.
   * @param {string} newValue - The new value of the attribute.
   * @returns {void}
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'value') {
      this._isControlled = newValue !== null
      const code = (newValue || '').split('').slice(0, 6)

      this._internalCode = [...code, ...new Array(6 - code.length).fill('')]
      this._updateInputs()
    }
  }

  // Add prop to focus (also for all other inputs)
  connectedCallback() {
    if (this._inputRefs[0]) {
      this._inputRefs[0].focus()
    }
  }

  /**
   * Emits input event
   * @returns {void}
   */
  _emitChange() {
    if (this.value.length > 5) {
      const event = new Event('input')
      this.dispatchEvent(event)
    }
  }

  /**
   * Handles change
   * @param {Event} e - The name of the attribute that changed.
   * @param {number} index - The old value of the attribute.
   *
   * @returns {void}
   */
  _handleChange(e, index) {
    e.stopPropagation()

    const el = /** @type {HTMLInputElement} */ (e.target)
    const inputValue = el.value.replace(/\D/g, '')

    if (!inputValue.length) return

    const digits = inputValue.split('')

    const newCode = [...this._internalCode]

    for (let i = 0; i < digits.length && index + i < 6; i++) {
      newCode[index + i] = digits[i]
    }

    this._internalCode = newCode
    this._updateInputs()
    this._emitChange()

    const moveTo = index + digits.length
    if (moveTo < 6) {
      this._inputRefs[moveTo].focus()
      return
    }
    this._inputRefs[5].focus()
  }

  /**
   * Handles key down
   * @param {KeyboardEvent} e - The name of the attribute that changed.
   * @param {number} index - The old value of the attribute.
   *
   * @returns {void}
   */
  _handleKeyDown(e, index) {
    e.stopPropagation()
    if (e.key === 'Backspace') {
      if (this._internalCode[index]) {
        this._internalCode[index] = ''
        this._updateInputs()
        this._emitChange()
      } else if (index > 0) {
        this._inputRefs[index - 1].focus()
      }
    }
  }

  /**
   * Handles paste
   * @param {ClipboardEvent} e - The name of the attribute that changed.
   *
   * @returns {void}
   */
  _handlePaste(e) {
    e.stopPropagation()

    if (!e.clipboardData) {
      return
    }

    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')
    if (pastedData.length === 6) {
      this._internalCode = pastedData.split('').slice(0, 6)
      this._updateInputs()
      this._emitChange()
      this._inputRefs[5].focus()
    }
    e.preventDefault()
  }

  /**
   * Updates inputs
   *
   * @returns {void}
   */
  _updateInputs() {
    for (let i = 0; i < 6; i++) {
      if (this._inputRefs[i]) {
        this._inputRefs[i].value = this._internalCode[i] || ''
      }
    }
  }
}
